import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { EmployeeTitle } from "@/Common/Models/Employee/Enums/EmployeeTitle";
import { Guid } from "@/Common/Models/Guid";
import { IPatientEnrollmentModel } from "@/Common/Models/Patient/UI/Profile/IPatientEnrollmentModel";
import { Person } from "@/Common/Models/Person/Person";
import { RAMQDispensateurClass } from "@/Common/Models/RAMQ/enums/RAMQDispensateurClass";
import { RAMQInfo } from "@/Common/Models/RAMQ/RAMQInfo";
import { RAMQInscription } from "@/Common/Models/RAMQ/RAMQInscription";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { cacheStore } from "@/Globals/Stores/CacheStore";
import { IPatientProfileEnrollmentComponentProps } from "./IPatientProfileComponentProps";

interface IPatientProfileEnrollmentResidentComponentState {
    EnrollmentType: string;
    ValidIPSNumber: boolean;
}

class PatientProfileEnrollmentResidentComponent extends BaseReactComponent<IPatientProfileEnrollmentComponentProps, IPatientProfileEnrollmentResidentComponentState> {
    public constructor(props: IPatientProfileEnrollmentComponentProps) {
        super(props);
        this.state = {
            EnrollmentType: "",
            ValidIPSNumber: true
        };
    }

    public render(): JSX.Element {
        const enrolmentModel = this.props.model;
        const ramqInfo = enrolmentModel && enrolmentModel.get("RAMQInscription");

        return this.GetResidentRender(ramqInfo, enrolmentModel, this.props.IsInAddMode || this.props.IsInEditMode);
    }

    private GetResidentRender(ramqInfo: RAMQInscription, model: IPatientEnrollmentModel, showAddEdit: boolean): JSX.Element {
        let residentRender: JSX.Element = null;

        const ramqInscription = model.get("RAMQInscription");

        let resolvedNameResidentOrIPS: string = "";
        let permitNumberToDisplay: string = "";
        let labelNom = cacheStore.GetString("NameLabel");

        if (ramqInscription && ramqInscription.ResidentPermit) {
            switch (ramqInscription.ResidentClass) {
                case RAMQDispensateurClass.Resident:
                    permitNumberToDisplay = `R${ramqInscription.ResidentPermit}`;
                    const residentName = RAMQInfo.ResolveClinicianName(permitNumberToDisplay);
                    resolvedNameResidentOrIPS = !IsNullOrWhiteSpace(residentName) ? residentName : "";
                    labelNom = cacheStore.GetString("NomDuResidentLabel");
                    break;

                case RAMQDispensateurClass.IPS:
                    const ramqNumber = `${RAMQDispensateurClass.IPS}${ramqInscription.ResidentPermit}`;
                    const ipsName = RAMQInfo.ResolveClinicianNameByRamqNumber(ramqNumber);
                    permitNumberToDisplay = ramqNumber;
                    resolvedNameResidentOrIPS = !IsNullOrWhiteSpace(ipsName) ? ipsName : "";
                    labelNom = "Nom de l'IPS";
                    break;

                default:
                    permitNumberToDisplay = ramqInscription.ResidentPermit;
                    break;
            }
        }

        let availableForEnrollment: Person[] = [];
        if (this.state.EnrollmentType === RAMQDispensateurClass.Resident) {
            if (model && model.get("AvailableResidents"))
                availableForEnrollment = model.get("AvailableResidents").toArray();
        }
        else {
            if (model && model.get("AvailableIPS"))
                availableForEnrollment = model.get("AvailableIPS").toArray();
        }

        if (showAddEdit) {
            residentRender = (
                <tbody>
                    <tr>
                        <td className="c1pad form">
                            <span>{cacheStore.GetString("TypeLabel")}</span>
                        </td>
                        <td className="c2">
                            <select onChange={(e: any) => this.handleSelectionChange(e.target.value)} className="form-control myle-control" value={this.state.EnrollmentType}>
                                <option value="" />
                                <option value="5">{cacheStore.GetString("ResidentLabel")}</option>
                                <option value="8">{cacheStore.GetString("IPSLabel")}</option>
                            </select>
                        </td>
                    </tr>

                    <tr>
                        <td className="c1pad form">
                            <span>{cacheStore.GetString("NameLabel")}</span>
                        </td>
                        <td className="c2">
                            <select
                                onChange={(e: any) => this.handleResidentChange(e.target.value)}
                                className={this.state.ValidIPSNumber == false ? "form-control myle-control invalid" : "form-control myle-control"}
                                value={model.get("SelectedResidentId") ? model.get("SelectedResidentId") : ""}
                            >
                                <option value=""> </option>
                                {availableForEnrollment?.map(p => (
                                    <option key={p.ContactFacetId} value={p.ContactFacetId}>
                                        {p.Name} {Employee.getIssuingOrgRule(p.EmployeeModel)?.Title === EmployeeTitle.NursePractitioner
                                            ? p.EmployeeModel.RamqNumber
                                            : p.EmployeeModel.FirstLicenceNumber
                                        }
                                    </option>
                                ))}
                            </select>
                            {this.renderIPSWarning()}
                        </td>
                    </tr>
                </tbody>
            );
        }
        else if (ramqInfo) {
            residentRender = (
                <tbody>
                    <tr>
                        <td className="c1pad form">
                            <span>{labelNom}</span>
                        </td>
                        <td className="c2">
                            <strong>{MyleHelper.ReplaceEmptyString(resolvedNameResidentOrIPS)}</strong>
                        </td>
                    </tr>
                    <tr>
                        <td className="c1pad form">
                            <span>{cacheStore.GetString("PermitNumberLabel")}</span>
                        </td>
                        <td className="c2">
                            <strong>{MyleHelper.ReplaceEmptyString(permitNumberToDisplay)}</strong>
                        </td>
                    </tr>
                </tbody>
            );
        }
        else {
            residentRender = (<div />);
        }

        return residentRender;
    }

    private handleResidentChange(id: Guid): void {
        const clinician = cacheStore.GetCliniciansPersonsByContactFacetId(id);
        this.mutateState(s =>
            s.ValidIPSNumber = s.EnrollmentType == RAMQDispensateurClass.IPS ? RAMQInfo.ValidateIPSRamqNumber(clinician?.RamqNumber) : true
        );

        sessionStore.dispatch(LocalPatientOperations.Profile.Enrollment_ResidentChange, { value: id });
    }

    private handleSelectionChange(type: string): void {
        this.mutateState(s => s.EnrollmentType = type);
    }

    private renderIPSWarning(): JSX.Element {
        if (!this.state.ValidIPSNumber) {
            return (
                <div className="c2">
                    <strong className="red-text">{cacheStore.GetString("ErrorIPSLabel")}</strong>
                </div>
            );
        }
        else
            return null;
    }
}

export { PatientProfileEnrollmentResidentComponent };
